import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  X,
  Boxes,
  Globe,
  ShieldCheck,
  Tag,
  Plus,
  Check,
  ShoppingCart,
  ArrowRight,
  Building2,
} from "lucide-react";
import {
  isInBasket,
  toggleBasket,
  basketCount,
  subscribeBasket,
} from "@/lib/programBasket";

/**
 * Right-hand drawer that shows a concise per-vendor service breakdown.
 * Replaces the standalone "Vendor Service Catalog" module — services live
 * inside each vendor's profile now.
 *
 * Selection model: each row is a checkbox-style add-to-basket toggle. Users
 * can select services across multiple vendors (the basket is global), then
 * close the drawer and either keep browsing or jump to Program Builder.
 *
 * Props:
 *   vendor: the Vendor object (or null/undefined to render closed).
 *   onClose: callback to dismiss the drawer.
 */
export default function VendorServicesSnapshot({ vendor, onClose }) {
  const nav = useNavigate();
  const [, force] = useState(0); // re-render on basket changes

  // Subscribe to basket changes so the per-row "Added" state stays accurate
  // even when the same vendor is open in two tabs (storage event).
  useEffect(() => {
    return subscribeBasket(() => force((x) => x + 1));
  }, []);

  // Lock body scroll while drawer is open + wire Escape-to-close so users
  // who toggled a service can dismiss the drawer with the keyboard. The
  // drawer's z-50 backdrop sits above the page-level basket bar (z-40), so
  // an explicit dismiss path is essential before the basket bar can be used.
  useEffect(() => {
    if (!vendor) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [vendor, onClose]);

  const services = useMemo(() => vendor?.services || [], [vendor]);

  if (!vendor) return null;

  const handleToggle = (svc) => {
    const item = {
      vendor_id: vendor.id,
      vendor_name: vendor.name,
      vendor_category: vendor.category,
      name: svc.name,
      description: svc.description,
      wholesale_cost: svc.wholesale_cost ?? null,
      suggested_retail_min: svc.suggested_retail_min ?? null,
      suggested_retail_max: svc.suggested_retail_max ?? null,
    };
    const wasIn = isInBasket(item);
    toggleBasket(item);
    toast.success(
      wasIn
        ? `Removed "${svc.name}" from your pending program.`
        : `Added "${svc.name}" to your pending program.`,
    );
  };

  const goToBuilder = () => {
    if (basketCount() === 0) {
      toast.message("Pick at least one service first.");
      return;
    }
    nav("/portal/programs?from=basket");
  };

  return (
    <div
      data-testid="vendor-services-snapshot"
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-labelledby="vss-title"
    >
      {/* Backdrop */}
      <div
        className="flex-1 bg-slate-900/40 backdrop-blur-[2px]"
        onClick={onClose}
        data-testid="vendor-services-backdrop"
      />
      {/* Panel */}
      <aside className="w-full sm:max-w-xl bg-white shadow-2xl overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-start gap-3">
            <span className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
              {vendor.logo_url ? (
                <img
                  src={vendor.logo_url}
                  alt=""
                  className="w-full h-full object-cover rounded-xl"
                />
              ) : (
                <Building2 className="w-5 h-5 text-slate-700" />
              )}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-blue-600">
                Vendor Services Snapshot
              </div>
              <h2
                id="vss-title"
                className="font-display text-xl font-semibold text-slate-900 truncate"
              >
                {vendor.name}
              </h2>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-600">
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-semibold capitalize">
                  <Tag className="w-2.5 h-2.5 text-slate-400" /> {vendor.category}
                  {vendor.subcategory && (
                    <span className="text-slate-500 font-normal">
                      · {vendor.subcategory}
                    </span>
                  )}
                </span>
                {vendor.regions && (
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    <Globe className="w-2.5 h-2.5" /> {vendor.regions}
                  </span>
                )}
                {vendor.compliance_attested && (
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                    <ShieldCheck className="w-2.5 h-2.5" /> Compliance
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="vendor-services-close"
              className="p-1.5 -mt-1 -mr-1 rounded-full hover:bg-slate-100"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
          {vendor.description && (
            <p className="mt-3 text-sm text-slate-700 leading-relaxed line-clamp-3">
              {vendor.description}
            </p>
          )}
        </div>

        {/* Services */}
        <div className="px-5 py-4 flex-1">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 inline-flex items-center gap-1">
              <Boxes className="w-3 h-3" /> Services offered
              <span className="ml-1.5 inline-flex items-center justify-center bg-slate-900 text-white rounded-full text-[10px] min-w-[1.25rem] h-5 px-1.5 font-bold">
                {services.length}
              </span>
            </div>
            <span className="text-[10px] text-slate-400">
              Tap a row to add it to your pending program
            </span>
          </div>

          {services.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-10 rounded-2xl border border-dashed border-slate-300 bg-slate-50">
              This vendor hasn't published a service catalog yet. Use{" "}
              <span className="font-semibold">Request intro</span> on the vendor
              card to learn more.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="vendor-services-list">
              {services.map((svc, i) => {
                const item = {
                  vendor_id: vendor.id,
                  vendor_name: vendor.name,
                  name: svc.name,
                };
                const inBasket = isInBasket(item);
                return (
                  <li
                    key={`${svc.name}-${i}`}
                    data-testid={`vendor-service-row-${i}`}
                  >
                    <button
                      type="button"
                      onClick={() => handleToggle(svc)}
                      className={`w-full text-left rounded-2xl border p-3.5 transition-all ${
                        inBasket
                          ? "border-blue-300 bg-blue-50/60 ring-1 ring-blue-100"
                          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
                      }`}
                      data-testid={`vendor-service-toggle-${i}`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`mt-0.5 flex items-center justify-center w-5 h-5 rounded-md border-2 shrink-0 transition-colors ${
                            inBasket
                              ? "bg-blue-600 border-blue-600 text-white"
                              : "bg-white border-slate-300"
                          }`}
                        >
                          {inBasket ? (
                            <Check className="w-3 h-3" strokeWidth={3} />
                          ) : (
                            <Plus className="w-3 h-3 text-slate-400" />
                          )}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-semibold text-slate-900 leading-snug">
                              {svc.name}
                            </span>
                            <span
                              className="text-xs font-semibold text-slate-700 tabular-nums ml-auto whitespace-nowrap"
                              data-testid={`vendor-service-bill-rate-${i}`}
                              title="Negotiated client bill rate (set by Nodera with the vendor)"
                            >
                              {svc.wholesale_cost != null
                                ? `$${svc.wholesale_cost}`
                                : "Contact for pricing"}
                            </span>
                          </div>
                          {svc.description && (
                            <p className="mt-1 text-xs text-slate-600 leading-relaxed line-clamp-2">
                              {svc.description}
                            </p>
                          )}
                          {svc.wholesale_cost != null && (
                            <div className="mt-1.5 text-[10px] uppercase tracking-wider text-slate-400">
                              Negotiated client bill rate
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Sticky footer — explicit "what's next" workflow guidance */}
        <div className="border-t border-slate-200 px-5 py-4 bg-slate-50 sticky bottom-0">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-slate-600">
              <div className="font-semibold text-slate-900 inline-flex items-center gap-1">
                <ShoppingCart className="w-3.5 h-3.5" /> Pending program
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Selections persist across vendors —{" "}
                <span className="font-semibold text-slate-700">
                  finalize details in Program Builder.
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={goToBuilder}
              data-testid="vendor-services-go-builder"
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-xs font-semibold hover:bg-slate-800"
            >
              Continue
              <span className="inline-flex items-center justify-center bg-blue-500 text-white rounded-full text-[10px] min-w-[1.25rem] h-4 px-1.5 font-bold">
                {basketCount()}
              </span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
